import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ROLE_OPTIONS as roles } from "@/constants/roles";

export function WorkerProfileCard({ profile }: { profile: any }) {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6 flex flex-col items-center gap-4">
      <div className="flex flex-col items-center gap-2">
        <div className="w-16 h-16 rounded-full overflow-hidden border-1 border-ember">
          <Avatar className="w-16 h-16">
            <AvatarImage src={profile.avatar_url} alt={profile.full_name} />
            <AvatarFallback>
              {profile.full_name ? profile.full_name[0] : t("workerCard.fallbackInitial")}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="text-xl font-semibold">{profile.full_name ?? ""}</div>
        <div className="text-xs text-slate">{t("workerCard.activeSinceFallback")}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 w-full mt-2">
        <div className="flex flex-col items-center bg-fog rounded-md py-2">
          <div className="text-xs text-muted-foreground">{t("workerCard.availability")}</div>
          <div className="font-bold text-ember">{t("workerCard.now")}</div>
        </div>
        <div className="flex flex-col items-center bg-fog rounded-md py-2">
          <div className="text-xs text-muted-foreground">{t("workerCard.shiftsCompleted")}</div>
          <div className="font-bold">0</div>
        </div>
        <div className="flex flex-col items-center bg-fog rounded-md py-2">
          <div className="text-xs text-muted-foreground">{t("workerCard.rating")}</div>
          <a href={`/app/profile/worker/${profile.user_id}/ratings`}>
            <div className="font-bold text-ember">
              {profile.total_review ?? ""}
            </div>
          </a>
        </div>
      </div>
      <div className="text-sm text-center mt-2 text-muted-foreground">
        {profile.about_me ?? t("workerCard.noBio")}
      </div>
      <div className="w-full mt-4">
        <div className="font-semibold text-sm mb-1">{t("workerCard.openTo")}</div>
        <div className="flex flex-wrap gap-2">
          {(profile.preferred_roles || []).map((roleValue: string) => {
            const roleObj = roles.find((r) => r.value === roleValue);
            return (
              <span
                key={roleValue}
                className="bg-fog text-garnet rounded-md px-3 py-1 text-xs font-medium border border-ember/30"
              >
                {roleObj ? roleObj.label : roleValue}
              </span>
            );
          })}
        </div>
      </div>
      <div className="w-full mt-4">
        <div className="font-semibold text-sm mb-1">{t("workerCard.specialities")}</div>
        <div className="flex flex-wrap gap-2">
          {(profile.skills || []).map((spec: string) => (
            <span
              key={spec}
              className="bg-fog text-garnet rounded-md px-3 py-1 text-xs font-medium border border-ember/30"
            >
              {spec}
            </span>
          ))}
        </div>
      </div>
      <div className="w-full flex flex-col gap-2 mt-6">
        <button
          className="w-full h-11 rounded-[25px] font-semibold text-garnet border border-garnet bg-white hover:bg-fog transition"
          onClick={() =>
            navigate({
              to: "/app/$role/messages",
              params: { role: "employer" },
              search: {
                tab: undefined,
                to: profile.user_id,
                businessId: undefined,
                selectedOfferId: undefined,
              },
            })
          }
        >
          {t("workerCard.sendMessage")}
        </button>
      </div>
    </div>
  );
}
